import React, { forwardRef, useState, useRef, useCallback, type MouseEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Check } from 'lucide-react';

import { UiLink } from '../lib/ui-link';
import { useViewportSize } from '../lib/viewport-size/index';
import { cn } from '../utils/cn';

import { Button, buttonVariants } from './button';
import { useInDrawer } from './drawer-mode-context';
import { PopoverDrawer } from './popover-drawer';
import { useDismiss } from './use-dismiss';
import { usePopoverPosition } from './use-popover-position';
import type { PopoverPosition, PopoverAlign } from './use-popover-position';

export type { PopoverPosition, PopoverAlign };

// ---------------------------------------------------------------------------
// PopoverContent — styled glass-morphism container
// ---------------------------------------------------------------------------
type PopoverSize = 'sm' | 'md' | 'lg' | 'sharing' | 'auto';

const popoverSizeClass: Record<PopoverSize, string> = {
	sm: 'w-[160px]',
	md: 'w-[240px]',
	lg: 'w-[320px]',
	sharing: 'w-[360px] max-w-[calc(100vw-16px)] max-h-[min(80vh,680px)] gap-0 overflow-y-auto rounded-lg p-0',
	// Fit content; cap so items can still truncate gracefully on extreme labels.
	auto: 'w-fit max-w-[320px] min-w-[140px]',
};

interface PopoverContentProps {
	children: React.ReactNode;
	size?: PopoverSize;
	className?: string;
	/** Optional pixel cap on the content height; pair with `overflow-y-auto` to scroll. */
	maxHeight?: number;
}

// mirrored-by: @marketing/modules/marketing/components/fixtures/fixture-views-lens-cells.tsx (FixtureViewsFourShapes, FixtureViewsScopedSharing - floating shell) - propagate visual changes
export const PopoverContent = ({ children, size = 'md', className, maxHeight }: PopoverContentProps) => {
	const inDrawer = useInDrawer();
	return (
		<div
			style={maxHeight !== undefined ? { maxHeight } : undefined}
			className={cn(
				'flex flex-col',
				// Roomier spacing for the touch rows of the bottom sheet.
				inDrawer && 'gap-0.5 p-2',
				// Desktop floating-box chrome — dropped in drawer mode, where the
				// bottom sheet already supplies surface, blur, shadow and rounding.
				// Dense on purpose: 4px padding + a 10px radius nest the 6px-radius
				// `list-sm` rows concentrically; a 1px gap keeps row fills from touching.
				!inDrawer && [
					'gap-px p-1 rounded-[10px] border border-border-weak bg-overlay backdrop-blur-[12px] shadow-popover overflow-clip',
					'animate-in fade-in-0 zoom-in-95 duration-150',
					popoverSizeClass[size],
				],
				className,
				// In drawer mode the bottom sheet owns width — neutralize any caller
				// width constraint (w-[Npx] / min-w / max-w) so content fills the
				// sheet. Placed after `className` so these win the tailwind-merge.
				inDrawer && 'w-full min-w-0 max-w-none'
			)}
		>
			{children}
		</div>
	);
};

// ---------------------------------------------------------------------------
// PopoverHeader — small section label inside PopoverContent (e.g. "MENTION")
// ---------------------------------------------------------------------------
interface PopoverHeaderProps {
	children: React.ReactNode;
	className?: string;
}

export const PopoverHeader = ({ children, className }: PopoverHeaderProps) => {
	return (
		<div
			className={cn(
				'px-2 py-1.5 text-[11px] font-medium text-neutral-weak uppercase tracking-wider',
				className,
			)}
		>
			{children}
		</div>
	);
};

// ---------------------------------------------------------------------------
// PopoverDivider — hairline between popover sections
// ---------------------------------------------------------------------------
interface PopoverDividerProps {
	className?: string;
}

export const PopoverDivider = ({ className }: PopoverDividerProps) => {
	// Square-ended and inset 4px past the popover padding, so the rule stops
	// short of the rounded corners instead of running into them.
	return <div className={cn('mx-1 my-1 h-px shrink-0 rounded-none bg-border-weak', className)} aria-hidden />;
};

// ---------------------------------------------------------------------------
// PopoverScrollList — capped scrollable list region inside PopoverContent
// ---------------------------------------------------------------------------
interface PopoverScrollListProps {
	children: React.ReactNode;
	className?: string;
}

export const PopoverScrollList = ({ children, className }: PopoverScrollListProps) => {
	const inDrawer = useInDrawer();
	return (
		<div
			className={cn(
				'flex max-h-[min(60vh,420px)] min-h-0 flex-col overflow-y-auto',
				inDrawer ? 'gap-0.5' : 'gap-px',
				className,
			)}
		>
			{children}
		</div>
	);
};

// ---------------------------------------------------------------------------
// PopoverItem — menu item button or link
// ---------------------------------------------------------------------------
const popoverItemVariantMap = {
	default: 'ghost-muted',
	danger: 'danger',
	active: 'solid-muted',
} as const;

// Two-tone menu rows: the label reads at full strength at rest and only the
// leading icon is dimmed (unless it sets its own `text-*`). Danger rows keep
// one tone - label and icon share the danger color.
const POPOVER_ROW_ICON_TONE_CLASS = "[&>svg:not([class*='text-'])]:text-neutral-muted";
export const popoverItemToneClass = {
	default: cn('text-neutral', POPOVER_ROW_ICON_TONE_CLASS),
	danger: '',
	active: POPOVER_ROW_ICON_TONE_CLASS,
} as const;

interface PopoverItemProps {
	children: React.ReactNode;
	onClick?: () => void;
	onMouseDown?: (e: MouseEvent<HTMLButtonElement>) => void;
	onMouseEnter?: () => void;
	href?: string;
	variant?: 'default' | 'danger' | 'active';
	size?: 'list-sm' | 'list-md' | 'list-lg';
	className?: string;
	disabled?: boolean;
	role?: string;
	'aria-selected'?: boolean;
	/** For `menuitemcheckbox` / `menuitemradio` rows, which take checked, not selected. */
	'aria-checked'?: boolean;
	'aria-label'?: string;
}

export const PopoverItem = forwardRef<HTMLButtonElement, PopoverItemProps>(
	(
		{
			children,
			onClick,
			onMouseDown,
			onMouseEnter,
			href,
			variant = 'default',
			// Compact list rows are the house style for popover menus (58 of 64
			// call sites set it explicitly) — default to it; opt UP via size.
			size = 'list-sm',
			className,
			disabled,
			role,
			'aria-selected': ariaSelected,
			'aria-checked': ariaChecked,
			'aria-label': ariaLabel,
		},
		ref
	) => {
		const variantStyle = popoverItemVariantMap[variant];
		const rowClassName = cn(popoverItemToneClass[variant], className);
		const inDrawer = useInDrawer();
		// Bump single-line rows to a >=44px touch target in drawer mode.
		// list-lg (multi-line card rows) is already tall enough — leave it.
		const resolvedSize =
			inDrawer && (size === 'list-sm' || size === 'list-md') ? 'list-touch' : size;

		if (href && !disabled) {
			return (
				<UiLink
					href={href}
					className={cn(buttonVariants({ variant: variantStyle, size: resolvedSize }), rowClassName)}
					onMouseEnter={onMouseEnter}
					role={role}
					aria-selected={ariaSelected}
					aria-checked={ariaChecked}
					aria-label={ariaLabel}
				>
					{children}
				</UiLink>
			);
		}

		// Menu rows are never a form's submit button, even when the popover
		// trigger sits inside a <form> (the routine dialog's filter row).
		return (
			<Button
				ref={ref}
				type="button"
				onClick={onClick}
				onMouseDown={onMouseDown}
				onMouseEnter={onMouseEnter}
				variant={variantStyle}
				size={resolvedSize}
				className={rowClassName}
				disabled={disabled}
				role={role}
				aria-selected={ariaSelected}
				aria-checked={ariaChecked}
				aria-label={ariaLabel}
			>
				{children}
			</Button>
		);
	}
);
PopoverItem.displayName = 'PopoverItem';

// ---------------------------------------------------------------------------
// PopoverCardItem — rich card row for feature/mode pickers.
// Layout: 32×32 icon container · label + optional description · optional check.
// Variants: ghost-muted (default) → solid-muted (active), via existing Button.
// ---------------------------------------------------------------------------
interface PopoverCardItemProps {
	icon: ReactNode;
	label: string;
	description?: string;
	active?: boolean;
	onClick?: () => void;
	disabled?: boolean;
	'aria-label'?: string;
}

export const PopoverCardItem = forwardRef<HTMLButtonElement, PopoverCardItemProps>(
	(
		{ icon, label, description, active, onClick, disabled, 'aria-label': ariaLabel },
		ref,
	) => {
		return (
			<Button
				ref={ref}
				onClick={onClick}
				disabled={disabled}
				variant={active ? 'solid-muted' : 'ghost-muted'}
				size="list-lg"
				aria-label={ariaLabel ?? label}
				aria-current={active ? 'true' : undefined}
			>
				<div className="size-8 rounded-md bg-neutral/5 flex items-center justify-center shrink-0 text-neutral">
					{icon}
				</div>
				<div className="flex flex-col gap-0.5 min-w-0 flex-1">
					<div className="text-[13px] font-semibold text-neutral leading-tight">
						{label}
					</div>
					{description && (
						<div className="text-[12px] text-neutral-muted leading-tight truncate">
							{description}
						</div>
					)}
				</div>
				{active && (
					<Check className="size-4 shrink-0 text-neutral-muted" aria-hidden="true" />
				)}
			</Button>
		);
	},
);
PopoverCardItem.displayName = 'PopoverCardItem';

// ---------------------------------------------------------------------------
// Popover — trigger-wrapping pattern (same API as old component)
// ---------------------------------------------------------------------------
interface PopoverProps {
	children: React.ReactNode;
	content: React.ReactNode;
	open?: boolean;
	onOpenChange?: (open: boolean) => void;
	position?: PopoverPosition;
	align?: PopoverAlign;
	offset?: number;
	className?: string;
	triggerClassName?: string;
	closeOnClickOutside?: boolean;
	closeOnEscape?: boolean;
}

export const Popover = ({
	children,
	content,
	open: controlledOpen,
	onOpenChange,
	position = 'bottom',
	align = 'end',
	offset = 4,
	className,
	triggerClassName,
	closeOnClickOutside = true,
	closeOnEscape = true
}: PopoverProps) => {
	const [internalOpen, setInternalOpen] = useState(false);
	const triggerRef = useRef<HTMLDivElement>(null);
	const contentRef = useRef<HTMLDivElement | null>(null);
	// Use state to track content element so hook re-runs when it mounts
	const [contentEl, setContentEl] = useState<HTMLDivElement | null>(null);

	// On mobile, present as a bottom drawer instead of a positioned popover.
	const { isMobile } = useViewportSize();
	const alreadyInDrawer = useInDrawer();

	const isControlled = controlledOpen !== undefined;
	const isOpen = isControlled ? controlledOpen : internalOpen;

	const setOpen = useCallback(
		(value: boolean) => {
			if (!isControlled) setInternalOpen(value);
			onOpenChange?.(value);
		},
		[isControlled, onOpenChange]
	);

	const close = useCallback(() => setOpen(false), [setOpen]);

	// Callback ref to capture content element when portal mounts
	const contentRefCallback = useCallback((node: HTMLDivElement | null) => {
		contentRef.current = node;
		setContentEl(node);
	}, []);

	// Desktop-only positioning + dismissal; no-op in drawer mode (Vaul owns both).
	const { positionStyle, ready } = usePopoverPosition({
		anchorEl: triggerRef.current,
		contentEl,
		open: isOpen && !isMobile,
		position,
		align,
		offset
	});

	useDismiss(isOpen && !isMobile, close, [triggerRef, contentRef], {
		closeOnClickOutside,
		closeOnEscape
	});

	const handleTriggerClick = (e: React.MouseEvent) => {
		// Portaled descendants - this popover's own sheet, or a nested popover's
		// portal / sheet - still bubble their clicks up the REACT tree into this
		// handler. Only a click that physically lands in the trigger toggles it;
		// anything else passes through untouched (no stopPropagation either, so
		// the sheet's document-level dismiss listener still sees the click).
		if (!triggerRef.current?.contains(e.target as Node)) return;
		e.stopPropagation();
		setOpen(!isOpen);
	};

	return (
		<>
			<div ref={triggerRef} onClick={handleTriggerClick} className={triggerClassName}>
				{children}
			</div>

			{isMobile ? (
				<PopoverDrawer
					open={isOpen}
					onClose={close}
					nested={alreadyInDrawer}
					dismissible={closeOnClickOutside}
				>
					{content}
				</PopoverDrawer>
			) : (
				typeof document !== 'undefined' &&
				isOpen &&
				createPortal(
					<div
						ref={contentRefCallback}
						className={cn('fixed z-[9999]', className)}
						style={{
							top: positionStyle.top,
							left: positionStyle.left,
							opacity: ready ? 1 : 0,
							pointerEvents: ready ? 'auto' : 'none'
						}}
						onClick={(e) => e.stopPropagation()}
					>
						{content}
					</div>,
					document.body
				)
			)}
		</>
	);
};

// ---------------------------------------------------------------------------
// PopoverAnchor — anchor-element pattern for externally-controlled popovers
// ---------------------------------------------------------------------------
interface PopoverAnchorProps {
	children: React.ReactNode;
	anchorEl: HTMLElement | null;
	open: boolean;
	onClose: () => void;
	position?: PopoverPosition;
	align?: PopoverAlign;
	offset?: number;
	className?: string;
	closeOnClickOutside?: boolean;
	closeOnEscape?: boolean;
}

export const PopoverAnchor = ({
	children,
	anchorEl,
	open,
	onClose,
	position = 'bottom',
	align = 'start',
	offset = 8,
	className,
	closeOnClickOutside = true,
	closeOnEscape = true
}: PopoverAnchorProps) => {
	const contentRef = useRef<HTMLDivElement | null>(null);
	const anchorRef = useRef<HTMLElement | null>(null);
	anchorRef.current = anchorEl;
	// Use state to track content element so hook re-runs when it mounts
	const [contentEl, setContentEl] = useState<HTMLDivElement | null>(null);

	// On mobile, present as a bottom drawer instead of an anchored popover.
	const { isMobile } = useViewportSize();
	const alreadyInDrawer = useInDrawer();

	// Callback ref to capture content element when portal mounts
	const contentRefCallback = useCallback((node: HTMLDivElement | null) => {
		contentRef.current = node;
		setContentEl(node);
	}, []);

	// Desktop-only positioning + dismissal; no-op in drawer mode (Vaul owns both).
	const { positionStyle, ready } = usePopoverPosition({
		anchorEl,
		contentEl,
		open: open && !isMobile,
		position,
		align,
		offset
	});

	useDismiss(open && !isMobile, onClose, [contentRef, anchorRef], {
		closeOnClickOutside,
		closeOnEscape
	});

	if (isMobile) {
		return (
			<PopoverDrawer
				open={open}
				onClose={onClose}
				nested={alreadyInDrawer}
				dismissible={closeOnClickOutside}
			>
				{children}
			</PopoverDrawer>
		);
	}

	if (!open || typeof document === 'undefined') return null;

	return createPortal(
		<div
			ref={contentRefCallback}
			className={cn('fixed z-[9999]', className)}
			style={{
				top: positionStyle.top,
				left: positionStyle.left,
				opacity: ready ? 1 : 0,
				pointerEvents: ready ? 'auto' : 'none'
			}}
			onClick={(e) => e.stopPropagation()}
		>
			{children}
		</div>,
		document.body
	);
};
